import React from "react";
import { Modal } from "react-bootstrap";
import DataEntryDetailsRightPane from "./DataEntryDetailsRightPane";
import "./RequestorContactModal.scss";

export interface RequestorContactModalProps {
    readonly show: boolean;
    readonly onClose: () => void;
    /**
     * The prop set DataEntryDetails passes to the classic-branch RightPane
     * (facility/transaction/user context). RightPane is self-connected for the
     * rest, so this mirrors that set; activeTabId/onChangeDataEntryTab are
     * overridden below.
     */
    readonly rightPaneProps: any;
    /**
     * Bumped by the parent on every Search click. Part of the pane's key, so clicking
     * Search always re-runs the search - even when the keys are unchanged from the
     * previous one.
     */
    readonly searchSignal?: number;
}

/**
 * Quick Entry requestor/contact host (AB#35116 Phase 1).
 *
 * ponytail: reuse the whole classic DataEntryDetailsRightPane at activeTabId=2
 * as the modal body. It renders RequestorContactPanel (the Phase 0 extraction)
 * with every search/select/assign/create handler already wired and carries its
 * own reduxForm(DATA_ENTRY_FIELDS_FORM) HOC, so the lifted <Field>s bind and
 * persist exactly like Classic. Parity by literal reuse, not a reimplementation.
 *
 * CHROMELESS (Randy 2026-07-24): the classic requestor/contact search grids are
 * themselves floating popups (CustomTable -> DragTableWrapper + .table-backdrop),
 * so a titled "Requestor Information & Assign Contact" dialog AROUND them read as
 * two stacked popups on the QE Search click. Header dropped and the whole host
 * hidden in SCSS - only the classic grid popup shows, exactly like classic mode.
 * The host stays mounted (it owns the form context the grids live in) and closes
 * itself once no grid is open (RightPane, hostContext==='requestorModal').
 *
 * Not rendered until opened, so RightPane's mount-time effects don't fire until
 * the specialist actually opens it.
 */
const RequestorContactModal: React.FC<RequestorContactModalProps> = (props) => {
    if (!props.show) {
        return null;
    }
    // searchSignal is forwarded (not used as a remount key): RightPane's auto-search
    // effect depends on it plus the fax/email seeds, so every Search re-runs the classic
    // search in the ALREADY-MOUNTED pane. The host stays mounted while the specialist
    // edits the row's inline boxes, which is why a mount-only seed left Search after
    // Reset re-running the PREVIOUS query. Re-seeding beats remounting here - a remount
    // would also re-fire every other mount effect the pane owns.
    return (
        <Modal show onHide={props.onClose} size="lg" centered backdrop={false} dialogClassName="requestor-contact-modal">
            <Modal.Body>
                <DataEntryDetailsRightPane
                    {...props.rightPaneProps}
                    searchSignal={props.searchSignal}
                    activeTabId={2}
                    hostContext="requestorModal"
                    onCloseHost={props.onClose}
                    onChangeDataEntryTab={() => { }}
                />
            </Modal.Body>
        </Modal>
    );
};

export default RequestorContactModal;
